import { UserAdviceFormat, UserAdviceLevel, UserAdvicePlacement, UserAdviceWidth } from "./Params"

// One advice as the format chooser sees it. Deliberately not the UserAdviceInstance
// itself: the chooser only needs the severity and where the button sits, which keeps
// it pure and testable without constructing advice classes.
export interface UserAdviceFormatInput {
  id: string;
  level: UserAdviceLevel;
  placement: UserAdvicePlacement;
}

// Severity ranking. Higher wins the space when the row has more advices than it can
// spell out, so a trailing alarm keeps its wording ahead of leading notices.
const levelRank: Record<UserAdviceLevel, number> = {
  [UserAdviceLevel.alarm]: 3,
  [UserAdviceLevel.warning]: 2,
  [UserAdviceLevel.notice]: 1,
};

// How many advices on one row may carry any wording at all. Past this the row is
// simply too crowded for text to help, so the rest fall back to their icons -- which
// is also the point at which the expand toggle becomes the way to read them.
const wordedLimit = 4;

// Only a notice this densely packed still earns its short wording; beyond it, notices
// are the first thing to give up their text since they are the least urgent.
const noticeWordedLimit = 2;

export const UserAdviceData = {
  // Pick a display format per advice (#1171).
  //
  // Deliberately a pure function of severity, placement, count and breakpoint rather
  // than a measurement of the rendered widths: the row clips on the right and offers
  // an expand toggle, so it never needs to know the true pixel cost -- and staying
  // pure keeps this unit- and golden-testable, and stable across languages (the same
  // row must not reshuffle itself just because French is wordier than English).
  //
  // The ordering rules run highest-reserve first: expanded and the day summary spell
  // everything out, then each advice gets what its severity affords at this width,
  // and finally anything past `wordedLimit` (ranked by severity, ties by position)
  // drops to its icon however urgent it is.
  adviceFormats(advices: UserAdviceFormatInput[], width: UserAdviceWidth, expanded: boolean): UserAdviceFormat[] {
    if (expanded) {
      return advices.map(() => UserAdviceFormat.full);
    }
    // The day summary renders its advices on a row of their own rather than sharing
    // the current log's, so nothing there competes for the space.
    if (advices.every((a) => a.placement == UserAdvicePlacement.day)) {
      return advices.map(() => UserAdviceFormat.full);
    }
    const noticeCount = advices.filter((a) => a.level == UserAdviceLevel.notice).length;
    const worded = UserAdviceData.wordedIds(advices);
    return advices.map((advice) => {
      if (advice.placement == UserAdvicePlacement.day) {
        return UserAdviceFormat.full;
      }
      if (!worded.has(advice.id)) {
        return UserAdviceFormat.icon;
      }
      return UserAdviceData.levelFormat(advice.level, width, noticeCount);
    });
  },

  // The ids allowed to carry wording at all: the `wordedLimit` most severe advices,
  // ties broken by the order they were given, so the row's leading buttons win.
  wordedIds(advices: UserAdviceFormatInput[]): Set<string> {
    return new Set(
      advices
        .map((advice, index) => ({ advice, index }))
        .toSorted((a, b) => levelRank[b.advice.level] - levelRank[a.advice.level] || a.index - b.index)
        .slice(0, wordedLimit)
        .map(({ advice }) => advice.id),
    );
  },

  // What a given severity affords at this width. An alarm is the one advice that
  // still spells itself out on a wide row; a warning settles for its short wording
  // and gives that up on the smallest screens; a notice only gets text on desktop,
  // and only while there are few enough of them to be worth reading.
  levelFormat(level: UserAdviceLevel, width: UserAdviceWidth, noticeCount: number): UserAdviceFormat {
    switch (level) {
      case UserAdviceLevel.alarm:
        return width == UserAdviceWidth.desktop ? UserAdviceFormat.full : UserAdviceFormat.short;
      case UserAdviceLevel.warning:
        return width == UserAdviceWidth.small ? UserAdviceFormat.icon : UserAdviceFormat.short;
      case UserAdviceLevel.notice:
        return width == UserAdviceWidth.desktop && noticeCount <= noticeWordedLimit
          ? UserAdviceFormat.short
          : UserAdviceFormat.icon;
    }
  },
};
